import Kendra from "aws-sdk/clients/kendra";
import _ from "lodash";
import {
  attributeValueEquals,
  attributeValueListContains,
  getValueType,
  DocumentAttributeValueTypeEnum,
  attributeValueToString,
  attributeValueToDate,
} from "./utils";
import { isNullOrEmpty } from "../utils";
import moment from "moment";


export class SelectedFacetManager {
  constructor(stringMap, dateRangeMap) {
    this.stringMap = stringMap;
    this.dateRangeMap = dateRangeMap;
  }

  static empty() {
    return new SelectedFacetManager({}, {});
  }

  isEmpty() {
    return _.isEmpty(this.stringMap) && _.isEmpty(this.dateRangeMap);
  }

  // Get

  getStringSelectionsOf(attributeKey) {
    return this.stringMap[attributeKey];
  }

  getDateRangeSelectionsOf(attributeKey) {
    return this.dateRangeMap[attributeKey];
  }

  isSelected(attributeKey,attributeValue) {
    const attributeValueList = this.getStringSelectionsOf(
      attributeKey
    )
    return attributeValueListContains(attributeValueList, attributeValue);
  }

  getAllSelected() {
    let res = {}
    // get all string type selections
    for (const attributeKey in this.stringMap) {
      if (this.stringMap.hasOwnProperty(attributeKey)) {
        const attributeList = this.stringMap[attributeKey];
        if (!_.isEmpty(attributeList)) {
          res[attributeKey] = attributeList;
        }
      }
    }

    // get all date type selections
    for (const attributeKey in this.dateRangeMap) {
      if (this.dateRangeMap.hasOwnProperty(attributeKey)) {
        let attributeList = [];
        const dateRange = this.dateRangeMap[attributeKey];
        if (dateRange && dateRange.min && dateRange.max) {
          attributeList.push({ DateValue: dateRange.min.toDate() });
          attributeList.push({ DateValue: dateRange.max.toDate() });
          res[attributeKey] = attributeList;
        }
      }
    }
    return res;
  }

  // Clear

  clear(
    attributeKey,
    attributeValue
  ) {
    let attributeValueList = this.getStringSelectionsOf(
      attributeKey
    );

    if (
      !isNullOrEmpty(attributeValueList) &&
      attributeValueListContains(attributeValueList, attributeValue)
    ) {
      attributeValueList = attributeValueList.filter(
        (item) => !attributeValueEquals(item, attributeValue)
      );
    }
    return new SelectedFacetManager(
      { ...this.stringMap, [attributeKey]: attributeValueList },
      this.dateRangeMap
    );
  }

  clearAll() {
    return new SelectedFacetManager({}, {});
  }

  clearSelectionsOf(attributeKey) {
    delete this.stringMap[attributeKey];
    return new SelectedFacetManager(this.stringMap, this.dateRangeMap);
  }

  clearDateSelectionsOf(attributeKey) {
    delete this.dateRangeMap[attributeKey];
    return new SelectedFacetManager(this.stringMap, this.dateRangeMap);
  }

  // Set

   set(
    attributeKey,
    attributeValue
  ) {
    const attributeValueList =
      this.getStringSelectionsOf(attributeKey) || [];
    if (!attributeValueListContains(attributeValueList, attributeValue)) {
      let newAttributeValueList = attributeValueList.slice();
      newAttributeValueList.push(attributeValue);
      return new SelectedFacetManager(
        { ...this.stringMap, [attributeKey]: newAttributeValueList },
        this.dateRangeMap
      );
    }
    return new SelectedFacetManager(this.stringMap, this.dateRangeMap);
  }

  setIsSelected(
    attributeKey,
    attributeValue,
    isSelected
  ) {
    if (isSelected) {
      return this.set(attributeKey, attributeValue);
    } else {
      return this.clear(attributeKey, attributeValue);
    }
  }

  setDateRange(
    attributeKey,
    value
  ) {
    if (value) {
      const minValue = moment(new Date(value[0], 0))
        .utc()
        .endOf("year")
        .millisecond(0);

      const maxValue = moment(new Date(value[1], 0))
        .utc()
        .endOf("year")
        .millisecond(0);

      return new SelectedFacetManager(this.stringMap, {
        ...this.dateRangeMap,
        [attributeKey]: { min: minValue, max: maxValue },
      });
    } else {
      return this.clearDateSelectionsOf(attributeKey);
    }
  }

  // filter builder
  buildAttributeFilter(
    attributeTypeLookup
  ) {
    const attributeList = this.getAllSelected();

    if (!attributeTypeLookup) {
      return undefined;
    }

    let filters = [];

    for (const attributeKey in attributeList) {
      if (attributeList.hasOwnProperty(attributeKey)) {
        const attributeValueList = attributeList[attributeKey];

        if (!_.isEmpty(attributeValueList)) {
          // Get index field type, or fall back to "seen" type
          const attribType =
            attributeTypeLookup[attributeKey] ||
            getValueType(attributeValueList[0]);
          const filter = this.selectAttributeFilterForAttributeList(
            attributeKey,
            attributeValueList,
            attribType
          );

          if (!isNullOrEmpty(filter)) {
            filters = filters.concat(filter);
          }
        }
      }
    }

    if (_.isEmpty(filters)) {
      return undefined;
    }

    return {
      AndAllFilters: filters,
    };
  }

   selectAttributeFilterForAttributeList = (
    attributeKey,
    attributeList,
    attributeType
  ) => {
    if (_.isEmpty(attributeList)) {
      return [];
    }

    // Handle special cases based on the *index field type* (NOT the "seen" type, may need to convert)
    if (attributeType === DocumentAttributeValueTypeEnum.DateValue) {
      return this.selectDateAttributeFilter(attributeKey, attributeList);
    } else if (
      attributeType === DocumentAttributeValueTypeEnum.StringListValue
    ) {
      return this.selectStringListAttributeFilter(attributeKey, attributeList);
    } else if (attributeType === DocumentAttributeValueTypeEnum.LongValue) {
      return this.selectLongAttributeFilter(attributeKey, attributeList);
    } else {
      let attributeFilterList = [];

      for (const attributeValue of attributeList) {
        attributeFilterList.push({
          EqualsTo: {
            Key: attributeKey,
            Value: attributeValue,
          },
        });
      }

      return [
        {
          OrAllFilters: attributeFilterList,
        },
      ];
    }
  };

  selectLongAttributeFilter = (
    attributeKey,
    attributeList
  ) => {
    let attributeFilterList = [];

    for (const attributeValue of attributeList) {
      if (attributeValue.LongValue) {
        attributeFilterList.push({
          EqualsTo: {
            Key: attributeKey,
            Value: { LongValue: attributeValue.LongValue },
          },
        });
      } else if (attributeValue.StringValue) {
        attributeFilterList.push({
          EqualsTo: {
            Key: attributeKey,
            Value: { StringValue: attributeValue.StringValue },
          },
        });
      } else {
        continue;
      }
    }

    return [
      {
        OrAllFilters: attributeFilterList,
      },
    ];
  };

  selectStringListAttributeFilter = (
    attributeKey,
    attributeList
  ) => {
    let stringValueList = [];
    for (const attributeValue of attributeList) {
      if (attributeValue && attributeValue.StringValue) {
        stringValueList.push(attributeValueToString(attributeValue));
      }
    }

    return [
      {
        ContainsAny: {
          Key: attributeKey,
          Value: { StringListValue: stringValueList },
        },
      },
    ];
  };

  selectDateAttributeFilter = (
    attributeKey,
    attributeList
  ) => {
    // If facet was returned as StringValue, convert to DateValue
    let dates = [];
    for (const attributeValue of attributeList) {
      const date = attributeValueToDate(attributeValue);
      if (date) {
        dates.push(date.toDate());
      }
    }

    if (_.isEmpty(dates)) {
      return [];
    }

    // Compute max range
    const range = {
      max: dates[0],
      min: dates[0],
    };

    for (const date of dates) {
      const dateValueMs = date.valueOf();
      if (dateValueMs < range.min.valueOf()) {
        range.min = date;
      }

      if (dateValueMs > range.max.valueOf()) {
        range.max = date;
      }
    }

    return [
      {
        AndAllFilters: [
          {
            GreaterThanOrEquals: {
              Key: attributeKey,
              Value: { DateValue: range.min },
            },
          },
          {
            LessThanOrEquals: {
              Key: attributeKey,
              Value: { DateValue: range.max },
            },
          },
        ],
      },
    ];
  };
}



// WEBPACK FOOTER //
// ./src/containers/Kendra/facets/SelectedFacetManager.js